import React, { useState, useEffect } from 'react';
import { userApi, storageApi } from '../utils/api';
import { User } from '../types/user';
import { StorageData } from '../types/storage';
import { format } from 'date-fns';
import UserForm from './UserForm';
import FileImport from './FileImport';

type TabType = 'users' | 'deleted' | 'data';

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [deletedRecords, setDeletedRecords] = useState<StorageData[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'deleted') {
      loadDeletedRecords();
    }
  }, [activeTab]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userApi.getAll();
      if (response.data?.data) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedRecords = async () => {
    try {
      setLoading(true);
      const response = await storageApi.getAll({ showDeleted: true });
      if (response.data?.data) {
        const deletedItems = response.data.data.filter(item => item.status === 'deleted');
        setDeletedRecords(deletedItems);
      }
    } catch (err) {
      setError('Silinmiş kayıtlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (userData: Partial<User>) => {
    try {
      if (selectedUser) {
        await userApi.update(selectedUser.id, userData);
      } else {
        await userApi.create(userData);
      }
      loadUsers();
      setSelectedUser(undefined);
    } catch (err) {
      setError('Kullanıcı kaydedilirken bir hata oluştu');
    }
  };

  const handleUserDelete = async (id: string) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        await userApi.delete(id);
        loadUsers();
      } catch (err) {
        setError('Kullanıcı silinirken bir hata oluştu');
      }
    }
  };

  const handleRestoreRecord = async (id: string) => {
    try {
      await storageApi.restore(id);
      loadDeletedRecords();
    } catch (err) {
      setError('Kayıt geri yüklenirken bir hata oluştu');
    }
  };

  const handleFileImport = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await storageApi.import(formData);
      
      if (response.data?.success) {
        setError('Veriler başarıyla içe aktarıldı');
      } else if (response.data?.error) {
        // Validation hatası durumunda işleme devam et
        if (response.data.error.includes('validation failed')) {
          setError('Bazı kayıtlar hatalı. Detaylar için aşağıdaki listeyi kontrol edin.');
          throw response.data; // Hata detaylarını FileImport bileşenine ilet
        } else {
          setError(response.data.error);
          throw response.data;
        }
      }
    } catch (err: any) {
      if (!err.response?.data?.error?.includes('validation failed')) {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || 'Dosya içe aktarılırken bir hata oluştu';
        setError(errorMessage);
      }
      throw err;
    }
  };

  const handleBackup = async () => {
    try {
      // Backup script'ini çalıştır
      await fetch('/api/backup', { method: 'POST' });
      setError('Yedekleme başarıyla tamamlandı');
    } catch (err) {
      setError('Yedekleme sırasında bir hata oluştu');
    }
  };

  const handleExport = async () => {
    try {
      const response = await storageApi.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kayitlar.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Dışa aktarma sırasında bir hata oluştu');
    }
  };

  const handleBulkDeactivate = async () => {
    const confirmed = window.confirm(
      'DİKKAT: Bu işlem tüm aktif kayıtları deaktive edecektir. Devam etmek istiyor musunuz?'
    );
    
    if (confirmed) {
      const password = window.prompt('Güvenlik için yönetici şifrenizi giriniz:');
      if (!password) return;

      try {
        await storageApi.bulkDeactivate(password);
        setError('Tüm kayıtlar başarıyla deaktive edildi');
        loadDeletedRecords();
      } catch (err: any) {
        setError(err.response?.data?.error || 'İşlem sırasında bir hata oluştu');
      }
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = window.confirm(
      'DİKKAT: Bu işlem tüm silinmiş kayıtları kalıcı olarak silecektir. Bu işlem geri alınamaz! Devam etmek istiyor musunuz?'
    );
    
    if (confirmed) {
      const password = window.prompt('Güvenlik için yönetici şifrenizi giriniz:');
      if (!password) return;

      try {
        await storageApi.bulkDelete(password);
        setError('Tüm silinmiş kayıtlar kalıcı olarak silindi');
        loadDeletedRecords();
      } catch (err: any) {
        setError(err.response?.data?.error || 'İşlem sırasında bir hata oluştu');
      }
    }
  };

  if (loading) return <div className="loading">Yükleniyor...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-panel">
      <div className="sticky-top bg-white border-bottom mb-4" style={{ top: '56px', zIndex: 1020 }}>
        <div className="container-fluid py-2">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Yönetim Paneli</h2>
            <div className="nav nav-pills">
              <button
                className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="bi bi-people me-1"></i>
                Kullanıcı Yönetimi
              </button>
              <button
                className={`nav-link ms-2 ${activeTab === 'deleted' ? 'active' : ''}`}
                onClick={() => setActiveTab('deleted')}
              >
                <i className="bi bi-trash me-1"></i>
                Silinmiş Kayıtlar
              </button>
              <button
                className={`nav-link ms-2 ${activeTab === 'data' ? 'active' : ''}`}
                onClick={() => setActiveTab('data')}
              >
                <i className="bi bi-database me-1"></i>
                Veri Yönetimi
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid pt-3">
        {activeTab === 'users' && (
          <div className="users-section">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">{selectedUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h3>
                <UserForm
                  initialData={selectedUser}
                  onSubmit={handleUserSubmit}
                  onCancel={() => setSelectedUser(undefined)}
                />
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 className="card-title">Kullanıcı Listesi</h3>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Kullanıcı Adı</th>
                        <th>Yetki</th>
                        <th>Oluşturulma</th>
                        <th>Güncelleme</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</td>
                          <td>{user.createdAt ? format(new Date(user.createdAt), 'dd.MM.yyyy HH:mm') : '-'}</td>
                          <td>{user.updatedAt ? format(new Date(user.updatedAt), 'dd.MM.yyyy HH:mm') : '-'}</td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm me-2"
                              onClick={() => setSelectedUser(user)}
                            >
                              <i className="bi bi-pencil me-1"></i>
                              Düzenle
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleUserDelete(user.id)}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'deleted' && (
          <div className="deleted-records-section">
            <div className="card">
              <div className="card-body">
                <h3 className="card-title">Silinmiş Kayıtlar</h3>
                {deletedRecords.length === 0 ? (
                  <p className="text-muted">Silinmiş kayıt bulunmamaktadır.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Müşteri</th>
                          <th>Plaka</th>
                          <th>Silinme Tarihi</th>
                          <th>İşlemler</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deletedRecords.map((record) => (
                          <tr key={record._id}>
                            <td>{record.no.toString().padStart(6, '0')}</td>
                            <td>{record.musteri}</td>
                            <td>{record.plaka}</td>
                            <td>{record.deletedAt ? format(new Date(record.deletedAt), 'dd.MM.yyyy HH:mm') : '-'}</td>
                            <td>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleRestoreRecord(record._id)}
                              >
                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                Geri Yükle
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="data-management-section">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Veri İçe/Dışa Aktarma</h3>
                <div className="row">
                  <div className="col-md-6">
                    <h4>İçe Aktar</h4>
                    <FileImport onImport={handleFileImport} />
                  </div>
                  <div className="col-md-6">
                    <h4>Dışa Aktar</h4>
                    <button 
                      className="btn btn-primary"
                      onClick={handleExport}
                    >
                      <i className="bi bi-download me-1"></i>
                      Excel Olarak İndir
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Toplu İşlemler</h3>
                <div className="row">
                  <div className="col-md-6">
                    <button 
                      className="btn btn-warning me-2"
                      onClick={handleBulkDeactivate}
                    >
                      <i className="bi bi-archive me-1"></i>
                      Tüm Kayıtları Deaktive Et
                    </button>
                  </div>
                  <div className="col-md-6">
                    <button 
                      className="btn btn-danger"
                      onClick={handleBulkDelete}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Silinmiş Kayıtları Kalıcı Olarak Sil
                    </button>
                  </div>
                </div>
                <small className="d-block mt-3 text-danger">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Bu işlemler geri alınamaz ve yönetici şifresi gerektirir.
                </small>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 className="card-title">Veritabanı Yedekleme</h3>
                <p className="text-muted">Veritabanının yedeğini almak için aşağıdaki butonu kullanın.</p>
                <button 
                  className="btn btn-warning"
                  onClick={handleBackup}
                >
                  <i className="bi bi-cloud-download me-1"></i>
                  Yedek Al
                </button>
                <small className="d-block mt-2 text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Yedekler /backup/mongodb klasöründe saklanır.
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel; 